import { Button } from '@askable/ui/core/button';
import { Input } from '@askable/ui/core/input';
import { useEffect, useId, useRef, useState } from 'react';
import { getHttpsUrl } from 'shared-utils';

import {
  pageLabels,
  pageMode,
  placements,
  sessionPageInsertLabel,
  sessionPageUrlHelp,
  sessionPageUrlTemplate,
} from './pages';
import type { FormEvent } from 'react';
import type { Pages, PageMode } from './pages';

type Props = {
  pages: Pages;
  defaults: Pages;
  saving: boolean;
  error: string | null;
  onCancel: () => void;
  onSave: (pages: Pages) => void;
};

export function SessionPagesForm({ pages, defaults, saving, error, onCancel, onSave }: Props) {
  const id = useId();
  const firstSelect = useRef<HTMLSelectElement>(null);
  useEffect(() => {
    firstSelect.current?.focus();
  }, []);
  const [draft, setDraft] = useState(pages);
  const [modes, setModes] = useState({ before: pageMode(pages.before), after: pageMode(pages.after) });
  const [validation, setValidation] = useState<string | null>(null);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (placements.some(p => modes[p] === 'custom' && !getHttpsUrl(draft[p]?.url))) {
      setValidation('Enter a valid HTTPS URL for each custom page.');
      return;
    }
    const page = (p: (typeof placements)[number]) => {
      if (modes[p] === 'default') return null;
      if (modes[p] === 'off') return { url: '', title: '', description: '' };
      return {
        url: draft[p]?.url?.trim() ?? '',
        title: draft[p]?.title?.trim() ?? '',
        description: draft[p]?.description?.trim() ?? '',
      };
    };
    onSave({ before: page('before'), after: page('after') });
  };
  return (
    <form onSubmit={submit}>
      <fieldset disabled={saving} className="flex flex-col gap-5">
        {placements.map(p => (
          <div key={p} className="flex flex-col gap-2">
            <label htmlFor={`${id}-${p}-mode`} className="text-sm font-medium">
              {pageLabels[p]}
            </label>
            <select
              ref={p === 'before' ? firstSelect : undefined}
              id={`${id}-${p}-mode`}
              className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={modes[p]}
              onChange={e => {
                const mode = e.target.value as PageMode;
                setModes(current => ({ ...current, [p]: mode }));
                setValidation(null);
              }}
            >
              <option value="default">Use study default</option>
              <option value="custom">Use a custom URL</option>
              <option value="off">Show no page</option>
            </select>
            {modes[p] === 'default' && (
              <p className="break-all text-xs text-muted-foreground">
                {defaults[p]?.url || 'No study default configured'}
              </p>
            )}
            {modes[p] === 'custom' && (
              <>
                <label htmlFor={`${id}-${p}-url`} className="text-xs">
                  HTTPS URL
                </label>
                <Input
                  id={`${id}-${p}-url`}
                  type="url"
                  required
                  value={draft[p]?.url ?? ''}
                  placeholder="https://"
                  aria-label={`${pageLabels[p]} URL`}
                  aria-describedby={`${id}-${p}-url-help`}
                  onChange={e => {
                    setDraft(current => ({ ...current, [p]: { ...current[p], url: e.target.value } }));
                    setValidation(null);
                  }}
                />
                <p id={`${id}-${p}-url-help`} className="text-xs text-muted-foreground">
                  {sessionPageUrlHelp}
                </p>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  aria-label={`${sessionPageInsertLabel} for ${pageLabels[p]}`}
                  onClick={() => {
                    setDraft(current => ({ ...current, [p]: { ...current[p], url: sessionPageUrlTemplate } }));
                    setValidation(null);
                  }}
                >
                  {sessionPageInsertLabel}
                </Button>
                <details className="text-xs">
                  <summary className="cursor-pointer text-muted-foreground">Customize text</summary>
                  {(['title', 'description'] as const).map(field => (
                    <label className="mt-3 block" key={field}>
                      {field === 'title' ? 'Title (optional)' : 'Description (optional)'}
                      <Input
                        className="mt-1"
                        value={draft[p]?.[field] ?? ''}
                        onChange={e =>
                          setDraft(current => ({ ...current, [p]: { ...current[p], [field]: e.target.value } }))
                        }
                      />
                    </label>
                  ))}
                </details>
              </>
            )}
          </div>
        ))}
        {(validation || error) && (
          <p role="alert" className="text-sm text-destructive">
            {validation || error}
          </p>
        )}
        <div className="flex justify-end gap-2 border-t pt-4">
          <Button type="button" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" variant="primary">
            {saving ? 'Saving…' : 'Save pages'}
          </Button>
        </div>
      </fieldset>
    </form>
  );
}
